import React, { useEffect, useMemo, useState } from 'react'
import ReactApexChart from 'react-apexcharts'
import {
  Alert, Collapse, Spinner, Table,
} from 'reactstrap'
import moment from 'moment'
import { fetchExtratoRhMes } from '../Dashboard/rhDashboardApi'
import {
  ActivityFlagBadges,
  DayFlagBadges,
  FERIADO_COLOR,
  PontoFlagBadges,
  hoursFromSeconds,
  isFeriadoPonto,
} from './flags'

const STACK_COLORS = [
  '#5664d2', '#34c38f', '#f1b44c', '#50a5f1', '#f46a6a',
  '#6f42c1', '#20c997', '#fd7e14', '#0dcaf0', '#adb5bd',
]

/**
 * Monta séries empilhadas: cada série = N-ésimo ponto do dia (slot).
 * meta[seriesIndex][dayIndex] → ponto (ou null).
 */
const buildStackedPontoChart = (dias) => {
  const categories = dias.map(d => moment(d.data).format('DD'))
  const maxSlots = dias.reduce((max, day) => {
    const n = Array.isArray(day.pontos) ? day.pontos.length : 0
    // Dia só com atividades (sem ponto): 1 slot sintético para permitir clique
    const slots = n > 0 ? n : ((day.tempo_dia_s || day.horas_ponto_dia_s || 0) > 0 ? 1 : 0)
    return Math.max(max, slots)
  }, 0)

  const meta = []
  const series = []

  for (let slot = 0; slot < maxSlots; slot += 1) {
    const slotMeta = []
    const data = dias.map((day) => {
      const pontos = Array.isArray(day.pontos) ? day.pontos : []
      if (pontos[slot]) {
        slotMeta.push(pontos[slot])
        return hoursFromSeconds(pontos[slot].tempo_s || 0)
      }
      // Fallback: dia sem pontos mas com horas de atividade → 1 segmento sintético
      if (slot === 0 && pontos.length === 0) {
        const secs = day.tempo_dia_s || day.horas_ponto_dia_s || 0
        if (secs > 0) {
          slotMeta.push({
            ponto_id: null,
            synthetic: true,
            tempo_s: secs,
            tempo_fmt: day.tempo_dia_fmt || day.horas_ponto_dia_fmt,
            ponto_entrada_fmt: null,
            ponto_saida_fmt: null,
            atividades: day.atividades || [],
          })
          return hoursFromSeconds(secs)
        }
      }
      slotMeta.push(null)
      return 0
    })
    meta.push(slotMeta)
    series.push({
      name: `Ponto ${slot + 1}`,
      data,
    })
  }

  return { categories, series, meta, maxSlots }
}

/**
 * Painel expandido de um mês: gráfico empilhado por ponto → tabela → atividades.
 */
const MonthAccordionPanel = ({ mes, graphId, isOpen }) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [extrato, setExtrato] = useState(null)
  const [selectedDay, setSelectedDay] = useState(null)
  const [expandedPontoId, setExpandedPontoId] = useState(null)

  useEffect(() => {
    if (!isOpen || !graphId || !mes) return undefined
    let cancelled = false
    const load = async () => {
      setLoading(true)
      setError(null)
      setSelectedDay(null)
      setExpandedPontoId(null)
      try {
        const data = await fetchExtratoRhMes(graphId, mes)
        if (cancelled) return
        if (data?.ok === false) {
          setExtrato(null)
          setError(data.msg || 'Erro ao carregar o mês.')
        } else {
          setExtrato(data)
        }
      } catch (err) {
        if (!cancelled) {
          setExtrato(null)
          setError(String(err?.message || err || 'Erro ao carregar o mês.'))
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [isOpen, graphId, mes])

  const dias = useMemo(() => {
    const list = Array.isArray(extrato?.dias) ? [...extrato.dias] : []
    return list.sort((a, b) => String(a.data).localeCompare(String(b.data)))
  }, [extrato])

  const stacked = useMemo(() => buildStackedPontoChart(dias), [dias])

  const chart = useMemo(() => {
    const { categories, series, meta } = stacked
    const selectSegment = (seriesIndex, dayIndex) => {
      if (dayIndex == null || dayIndex < 0 || !dias[dayIndex]) return
      const day = dias[dayIndex]
      setSelectedDay(day)
      const ponto = meta[seriesIndex]?.[dayIndex]
      if (ponto && ponto.ponto_id != null) {
        setExpandedPontoId(ponto.ponto_id)
      } else {
        setExpandedPontoId(null)
      }
    }

    return {
      series,
      options: {
        chart: {
          type: 'bar',
          stacked: true,
          toolbar: { show: false },
          events: {
            dataPointSelection: (_event, _ctx, config) => {
              selectSegment(config?.seriesIndex, config?.dataPointIndex)
            },
            click: (_event, _ctx, config) => {
              // Fallback: clique na área da coluna (alguns browsers só disparam click)
              if (config?.dataPointIndex == null || config.dataPointIndex < 0) return
              if (config?.seriesIndex == null || config.seriesIndex < 0) return
              selectSegment(config.seriesIndex, config.dataPointIndex)
            },
          },
        },
        plotOptions: {
          bar: {
            columnWidth: '55%',
            borderRadius: 2,
          },
        },
        colors: [
          ({ seriesIndex, dataPointIndex }) => {
            const ponto = meta[seriesIndex]?.[dataPointIndex]
            if (isFeriadoPonto(ponto)) return FERIADO_COLOR
            return STACK_COLORS[seriesIndex % STACK_COLORS.length]
          },
        ],
        dataLabels: { enabled: false },
        legend: {
          show: series.length > 1,
          position: 'top',
          fontSize: '11px',
        },
        xaxis: {
          categories,
          title: { text: 'Dia' },
        },
        yaxis: {
          title: { text: 'Horas' },
          labels: {
            formatter: v => Number(v).toFixed(1),
          },
        },
        tooltip: {
          shared: false,
          intersect: true,
          y: {
            formatter: (v, opts) => {
              const sIdx = opts?.seriesIndex
              const dIdx = opts?.dataPointIndex
              const ponto = meta[sIdx]?.[dIdx]
              if (!ponto) return `${Number(v).toFixed(2)}h`
              const ini = ponto.ponto_entrada_fmt || '—'
              const fim = ponto.ponto_saida_fmt || '—'
              const tempo = ponto.tempo_fmt || `${Number(v).toFixed(2)}h`
              if (isFeriadoPonto(ponto)) return `Feriado: ${tempo}`
              if (ponto.synthetic) return `Atividades: ${tempo}`
              return `${tempo} (${ini} → ${fim})`
            },
          },
        },
        states: {
          active: {
            allowMultipleDataPointsSelection: false,
          },
        },
      },
    }
  }, [dias, stacked])

  const pontosDoDia = selectedDay?.pontos || []

  return (
    <tr className="activities-month-drawer">
      <td colSpan={8} className="p-0 border-top-0">
        <Collapse isOpen={isOpen}>
          <div className="p-3" style={{ background: '#fafbfc' }}>
            {loading && (
              <div className="text-center py-4"><Spinner color="primary" size="sm" /></div>
            )}
            {!loading && error && <Alert color="danger" className="mb-0">{error}</Alert>}
            {!loading && !error && dias.length === 0 && (
              <p className="text-muted mb-0 text-center py-3">Sem atividades neste mês.</p>
            )}
            {!loading && !error && dias.length > 0 && (
              <>
                <div className="mb-2 small text-muted">
                  Colunas empilhadas por ponto (bloco de horário). Clique em um segmento para abrir o ponto.
                </div>
                {stacked.series.length === 0 ? (
                  <p className="text-muted small">Sem horas registradas neste mês para o gráfico.</p>
                ) : (
                  <ReactApexChart
                    options={chart.options}
                    series={chart.series}
                    type="bar"
                    height={280}
                  />
                )}

                {selectedDay && (
                  <div className="mt-3">
                    <div className="d-flex align-items-center mb-2 flex-wrap">
                      <strong className="mr-2">
                        Dia {selectedDay.data_fmt}
                        {' '}
                        (
                        {selectedDay.tempo_dia_fmt || selectedDay.horas_ponto_dia_fmt || '—'}
                        )
                      </strong>
                      <DayFlagBadges day={selectedDay} />
                    </div>

                    {pontosDoDia.length === 0 && (
                      <div>
                        <p className="small text-muted mb-2">
                          Nenhum ponto (início/fim) neste dia. Atividades:
                        </p>
                        <Table size="sm" bordered responsive className="mb-0 bg-white">
                          <thead>
                            <tr>
                              <th>Projeto</th>
                              <th>Atividade</th>
                              <th>Descrição</th>
                              <th>Tempo</th>
                              <th>Flags</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(selectedDay.atividades || []).map(ativ => (
                              <tr key={ativ.pontoAtiv_id}>
                                <td>{ativ.projeto_nome}</td>
                                <td>{ativ.pontoAtiv_ativ}</td>
                                <td>{ativ.pontoAtiv_desc}</td>
                                <td>{ativ.pontoAtiv_tempo}</td>
                                <td><ActivityFlagBadges activity={ativ} /></td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    )}

                    {pontosDoDia.length > 0 && (
                      <Table size="sm" bordered responsive className="mb-0 bg-white">
                        <thead>
                          <tr>
                            <th style={{ width: 36 }} />
                            <th>Início</th>
                            <th>Fim</th>
                            <th>Tempo</th>
                            <th>Atividades</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pontosDoDia.map((ponto) => {
                            const open = expandedPontoId === ponto.ponto_id
                            const feriado = isFeriadoPonto(ponto)
                            return (
                              <React.Fragment key={ponto.ponto_id}>
                                <tr
                                  role="button"
                                  style={{
                                    cursor: 'pointer',
                                    background: open
                                      ? '#eef2ff'
                                      : (feriado ? '#d1ecf1' : undefined),
                                  }}
                                  onClick={() => setExpandedPontoId(open ? null : ponto.ponto_id)}
                                >
                                  <td>
                                    <i className={`mdi ${open ? 'mdi-chevron-down' : 'mdi-chevron-right'}`} />
                                  </td>
                                  <td>
                                    {ponto.ponto_entrada_fmt || '—'}
                                    <PontoFlagBadges ponto={ponto} />
                                  </td>
                                  <td>{ponto.ponto_saida_fmt || '—'}</td>
                                  <td>{ponto.tempo_fmt || '—'}</td>
                                  <td>{(ponto.atividades || []).length}</td>
                                </tr>
                                {open && (
                                  <tr>
                                    <td colSpan={5} style={{ background: '#f8f9fa' }}>
                                      {(ponto.atividades || []).length === 0 ? (
                                        <div className="p-2 text-muted small">
                                          Sem itens de pontoAtiv neste ponto.
                                        </div>
                                      ) : (
                                        <Table size="sm" bordered className="mb-0 bg-white">
                                          <thead>
                                            <tr>
                                              <th>Projeto</th>
                                              <th>Atividade</th>
                                              <th>Descrição</th>
                                              <th>Tempo</th>
                                              <th>Flags</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {ponto.atividades.map(ativ => (
                                              <tr key={ativ.pontoAtiv_id}>
                                                <td>{ativ.projeto_nome}</td>
                                                <td>{ativ.pontoAtiv_ativ}</td>
                                                <td>{ativ.pontoAtiv_desc}</td>
                                                <td>{ativ.pontoAtiv_tempo}</td>
                                                <td><ActivityFlagBadges activity={ativ} /></td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </Table>
                                      )}
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            )
                          })}
                        </tbody>
                      </Table>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </Collapse>
      </td>
    </tr>
  )
}

export default MonthAccordionPanel
